import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Settings as SettingsIcon, Calendar as CalendarIcon, Users, BarChart3, CalendarDays, CheckCircle, Package, Landmark, Briefcase, Building2, LogOut, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/currency";
import StatsCards from "@/components/orders/StatsCards";
import OrderList from "@/components/orders/OrderList";
import UpcomingDeadlines from "@/components/orders/UpcomingDeadlines";
import WorkflowOverview from "@/components/orders/WorkflowOverview";
import AddOrderModal from "@/components/orders/AddOrderModal";
import EditOrderModal from "@/components/orders/EditOrderModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [workflows, setWorkflows] = useState({ sublimation: [], embroidery: [] });
  const [loading, setLoading] = useState(true);
  const [ordersLoaded, setOrdersLoaded] = useState(false); // Track if orders have been loaded
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMethod, setFilterMethod] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [statsMonth, setStatsMonth] = useState("all"); // 'all' or 'YYYY-MM'
  const [orderMonths, setOrderMonths] = useState([]); // Months that have orders
  const navigate = useNavigate();
  const { isOwner, user, logout } = useAuth();

  // Generate month options - memoized to prevent recalculation
  const monthOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Time" }];
    const monthSet = new Set();
    const now = new Date();
    
    // Add last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      monthSet.add(value);
    }
    
    // Add months from orders
    orderMonths.forEach(month => monthSet.add(month));
    
    // Sort months descending and create options
    const sortedMonths = Array.from(monthSet).sort((a, b) => b.localeCompare(a));
    sortedMonths.forEach(value => {
      const [year, month] = value.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    });
    
    return options;
  }, [orderMonths]);

  // Fetch orders
  const fetchOrders = async () => {
    try {
      console.log('[Dashboard] Fetching orders from:', `${API}/orders`);
      const response = await axios.get(`${API}/orders`);
      console.log('[Dashboard] Orders response:', response.data);
      
      // Handle both array response and paginated response format
      const ordersData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || response.data?.orders || []);
      
      console.log('[Dashboard] Parsed orders count:', ordersData.length);
      
      // Extract unique months from orders for the filter dropdown
      const months = [...new Set(ordersData.map(order => order.created_at?.slice(0, 7)).filter(Boolean))];
      setOrderMonths(months);
      
      // Filter out delivered orders from main dashboard
      const activeOrders = ordersData.filter(order => order.current_status !== 'Delivered');
      console.log('[Dashboard] Active orders (non-Delivered):', activeOrders.length);
      
      // Set both states together
      setOrders(activeOrders);
      setFilteredOrders(activeOrders);
      setOrdersLoaded(true);
      console.log('[Dashboard] Orders state updated, ordersLoaded=true');
    } catch (error) {
      console.error("[Dashboard] Error fetching orders:", error);
      toast.error("Failed to fetch orders");
      setOrders([]);
      setFilteredOrders([]);
      setOrdersLoaded(true);
    }
  };

  // Fetch stats with optional month filter
  const fetchStats = async (month = "all") => {
    try {
      const url = month === "all" 
        ? `${API}/orders/stats/dashboard`
        : `${API}/orders/stats/dashboard?month=${month}`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch settings/workflows once (using minimal endpoint to avoid large logo payload)
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/minimal`);
      setWorkflows({
        sublimation: response.data.sublimation_workflow || [],
        embroidery: response.data.embroidery_workflow || []
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      // Fallback to default workflows if settings fails
      setWorkflows({
        sublimation: ['Design', 'Printing', 'Sublimation', 'Pressing', 'Finishing', 'Quality Check', 'Delivered'],
        embroidery: ['Design', 'Approved', 'In Production', 'Finishing', 'Quality Check', 'Delivered']
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch orders first - this is critical
        await fetchOrders();
        
        // Fetch stats and settings in parallel, but don't fail if they error
        await Promise.allSettled([fetchStats(statsMonth), fetchSettings()]);
      } catch (error) {
        console.error('[Dashboard] Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch stats when month filter changes (skip initial render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchStats(statsMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsMonth]);

  // Apply filters (including month filter from stats) - only run after orders loaded
  useEffect(() => {
    // Skip filtering until orders have been loaded at least once
    if (!ordersLoaded) {
      console.log('[Dashboard] Skipping filter - orders not loaded yet');
      return;
    }
    
    let filtered = [...orders];
    
    console.log('[Dashboard] Applying filters:', { 
      totalOrders: orders.length, 
      statsMonth, 
      searchTerm, 
      filterMethod, 
      filterStatus 
    });

    // Month filter (sync with stats month)
    if (statsMonth !== "all") {
      filtered = filtered.filter((order) => {
        const orderMonth = order.created_at?.slice(0, 7);
        return orderMonth === statsMonth;
      });
      console.log('[Dashboard] After month filter:', filtered.length);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (order.email && order.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          order.product_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Method filter
    if (filterMethod !== "all") {
      filtered = filtered.filter((order) => order.printing_method === filterMethod);
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((order) => order.current_status === filterStatus);
    }

    console.log('[Dashboard] Final filtered orders:', filtered.length);
    setFilteredOrders(filtered);
  }, [searchTerm, filterMethod, filterStatus, orders, statsMonth, ordersLoaded]);

  const handleOrderCreated = async (newOrder) => {
    console.log('[Dashboard] Order created, refreshing data...');
    setShowAddModal(false);
    
    // If we received the new order from the modal, add it immediately to avoid lag
    if (newOrder && newOrder.id) {
      console.log('[Dashboard] Adding new order directly:', newOrder.client_name);
      const updatedOrders = [newOrder, ...orders];
      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);
      
      // Update months
      const months = [...new Set(updatedOrders.map(order => order.created_at?.slice(0, 7)).filter(Boolean))];
      setOrderMonths(months);
    }
    
    // Reset filters
    setStatsMonth("all");
    setSearchTerm("");
    setFilterMethod("all");
    setFilterStatus("all");
    
    // Fetch fresh data in background with a small delay to allow DB sync
    setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/orders`);
        const ordersData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || response.data?.orders || []);
        
        console.log('[Dashboard] Background refresh - Total orders:', ordersData.length);
        
        const months = [...new Set(ordersData.map(order => order.created_at?.slice(0, 7)).filter(Boolean))];
        setOrderMonths(months);
        
        const activeOrders = ordersData.filter(order => order.current_status !== 'Delivered');
        console.log('[Dashboard] Background refresh - Active orders:', activeOrders.length);
        
        setOrders(activeOrders);
        setFilteredOrders(activeOrders);
      } catch (error) {
        console.error('[Dashboard] Background refresh error:', error);
      }
    }, 1000); // 1 second delay for DB sync
    
    // Update stats
    await fetchStats("all");
  };

  const handleOrderUpdated = () => {
    fetchOrders();
    fetchStats();
    setEditingOrder(null);
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted successfully");
      fetchOrders();
      fetchStats();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      
      // Check if moving from Delivered to another status
      if (order && order.current_status === 'Delivered' && newStatus !== 'Delivered') {
        const confirmMove = window.confirm(
          `This order is marked as Delivered. Moving it back to "${newStatus}" will remove it from "Projects Done" and bring it back to active orders. Are you sure?`
        );
        if (!confirmMove) {
          return; // Cancel the status update
        }
      }
      
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success("Status updated successfully");
      fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header Skeleton */}
        <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Skeleton className="h-8 w-64 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-10 w-32" />
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-28" />
              ))}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-6 py-8">
          {/* Stats Skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-card border border-border rounded-md p-4">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>

          {/* Search Skeleton */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-10 w-48" />
          </div>

          {/* Content Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card border border-border rounded-md p-6">
                  <div className="flex gap-4">
                    <Skeleton className="h-24 w-24 rounded-md" />
                    <div className="flex-1">
                      <Skeleton className="h-6 w-48 mb-2" />
                      <Skeleton className="h-4 w-32 mb-4" />
                      <div className="grid grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((j) => (
                          <div key={j}>
                            <Skeleton className="h-3 w-16 mb-1" />
                            <Skeleton className="h-5 w-20" />
                          </div>
                        ))}
                      </div>
                      <Skeleton className="h-2 w-full mt-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-md p-4">
                <Skeleton className="h-6 w-40 mb-4" />
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full mb-2" />
                ))}
              </div>
              <div className="bg-card border border-border rounded-md p-4">
                <Skeleton className="h-6 w-40 mb-4" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-40 no-print">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-extrabold tracking-tight text-primary">
                Production Dashboard
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Track and manage your orders</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-primary hover:bg-primary/90 font-medium"
                data-testid="add-order-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Order
              </Button>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <Button
              variant="outline"
              onClick={() => navigate("/calendar")}
              className="whitespace-nowrap"
              data-testid="nav-calendar"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Calendar
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/customers")}
              className="whitespace-nowrap"
              data-testid="nav-customers"
            >
              <Users className="h-4 w-4 mr-2" />
              Customers
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/projects-done")}
              className="whitespace-nowrap"
              data-testid="nav-projects-done"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Projects Done
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/reports")}
              className="whitespace-nowrap"
              data-testid="nav-reports"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Reports
            </Button>
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate("/finance")}
                className="whitespace-nowrap"
                data-testid="nav-finance"
              >
                <Landmark className="h-4 w-4 mr-2" />
                Finance
              </Button>
            )}
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate("/payroll")}
                className="whitespace-nowrap"
                data-testid="nav-payroll"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Payroll
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/business-info")}
              className="whitespace-nowrap"
              data-testid="nav-business-info"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Business Info
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/quotation")}
              className="whitespace-nowrap"
              data-testid="nav-quotation"
            >
              <FileText className="h-4 w-4 mr-2" />
              Quotation
            </Button>
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate("/settings")}
                className="whitespace-nowrap"
                data-testid="nav-settings"
              >
                <SettingsIcon className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-border">
              <span className="text-sm text-muted-foreground">
                {user?.full_name} <span className="text-xs bg-secondary px-2 py-0.5 rounded capitalize">{user?.role}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <StatsCards 
          stats={stats} 
          statsMonth={statsMonth}
          setStatsMonth={setStatsMonth}
          monthOptions={monthOptions}
        />

        {/* Today's Activity Summary - only show when viewing all time or current month */}
        {orders.length > 0 && (() => {
          const today = new Date().toISOString().slice(0, 10);
          const currentMonth = new Date().toISOString().slice(0, 7);
          
          // Only show today's activity when viewing "all" or current month
          if (statsMonth !== "all" && statsMonth !== currentMonth) {
            return null;
          }
          
          // Get ALL orders (not just active) for today's count
          const allOrders = [...orders];
          // Also count delivered orders that were created today from the full list
          const todayOrders = allOrders.filter(o => o.created_at?.slice(0, 10) === today);
          const todaySales = todayOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
          const todayQty = todayOrders.reduce((sum, o) => sum + (o.total_quantity || 0), 0);
          
          if (todayOrders.length > 0) {
            return (
              <div className="mt-6 p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary text-primary-foreground p-2 rounded-full">
                      <CalendarDays className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold">Today's Activity</h3>
                      <p className="text-sm text-muted-foreground">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-heading font-bold text-primary">{todayOrders.length}</p>
                      <p className="text-xs text-muted-foreground">Orders</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-heading font-bold text-purple-600">{todayQty}</p>
                      <p className="text-xs text-muted-foreground">Items</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-heading font-bold text-green-600">{formatCurrency(todaySales)}</p>
                      <p className="text-xs text-muted-foreground">Sales</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Search and Filters */}
        <div className="mt-8 mb-6 flex flex-col md:flex-row gap-4 no-print">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by client, email, or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger className="w-full md:w-48" data-testid="filter-method">
              <SelectValue placeholder="All Methods" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="Sublimation">Sublimation</SelectItem>
              <SelectItem value="Embroidery">Embroidery</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Design">Design</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Sizing">Sizing</SelectItem>
              <SelectItem value="Print">Print</SelectItem>
              <SelectItem value="Press">Press</SelectItem>
              <SelectItem value="Program">Program</SelectItem>
              <SelectItem value="Sew">Sew</SelectItem>
              <SelectItem value="Embroidered">Embroidered</SelectItem>
              <SelectItem value="Pack">Pack</SelectItem>
              <SelectItem value="Delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Order List - Takes 2 columns on large screens */}
          <div className="lg:col-span-2">
            {filteredOrders.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-12 text-center" data-testid="empty-orders-message">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-heading font-semibold mb-2">No active orders</h3>
                <p className="text-muted-foreground mb-4">
                  {orders.length === 0 
                    ? "Start by creating your first order" 
                    : "No orders match your search criteria"}
                </p>
                {orders.length === 0 && (
                  <Button onClick={() => setShowAddModal(true)} className="bg-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Order
                  </Button>
                )}
              </div>
            ) : (
              <OrderList
                orders={filteredOrders}
                workflows={workflows}
                onEdit={setEditingOrder}
                onDelete={handleDeleteOrder}
                onStatusUpdate={handleStatusUpdate}
              />
            )}
          </div>

          {/* Right Sidebar - Takes 1 column */}
          <div className="space-y-6">
            <UpcomingDeadlines orders={orders} />
            <WorkflowOverview orders={orders} />
          </div>
        </div>
      </main>

      {/* Modals */}
      {showAddModal && (
        <AddOrderModal
          onClose={() => setShowAddModal(false)}
          onOrderCreated={handleOrderCreated}
        />
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}
